// SAD-863: a comment thread on a deliverable, a deliverable date or a work
// product. It replaces the free-text notes those used to carry: the old note
// comes back from the server as the thread's first, imported comment.
//
// Everyone who can read the program reads and comments. A comment shows its
// author's initials avatar, name and time; it takes upvotes (one per person,
// a toggle) and replies (one level deep). Only its author edits or deletes it.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraAvatar, SpecraBadge, SpecraButton, SpecraSectionHeader, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { formatDistanceToNowStrict } from 'date-fns';
import { ThumbsUp } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../../../../contexts/authContext';
import { useProgramWriteAccess } from '../../Programs/programWriteAccess';
import { useComments, useCreateComment, useDeleteComment, useSetCommentUpvote, useUpdateComment } from '../hooks';
const IMPORTED_AUTHOR = 'Imported note';
function authorOf(c) {
    return c.authorName || (c.imported ? IMPORTED_AUTHOR : 'Former user');
}
function when(iso) {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : formatDistanceToNowStrict(d, {
        addSuffix: true
    });
}
function Composer({ placeholder, initial = '', submitLabel, busy, onSubmit, onCancel, textareaTestId, submitTestId, autoFocus }) {
    const [text, setText] = useState(initial);
    const ready = text.trim() !== '' && text.trim() !== initial.trim() && !busy;
    const submit = async ()=>{
        if (!ready) return;
        try {
            await onSubmit(text.trim());
            setText('');
        } catch  {
        // The mutation toasts the failure; the text stays for another try.
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-[var(--space-1-5)]",
        children: [
            /*#__PURE__*/ _jsx(SpecraTextarea, {
                rows: 2,
                value: text,
                placeholder: placeholder,
                "aria-label": placeholder,
                autoFocus: autoFocus,
                onChange: (e)=>setText(e.target.value),
                onKeyDown: (e)=>{
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void submit();
                },
                "data-testid": textareaTestId
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        disabled: !ready,
                        onClick: ()=>void submit(),
                        "data-testid": submitTestId,
                        children: busy ? 'Saving…' : submitLabel
                    }),
                    onCancel ? /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "ghost",
                        onClick: onCancel,
                        children: "Cancel"
                    }) : null
                ]
            })
        ]
    });
}
function CommentItem({ c, replies, actions }) {
    const { programId, target, me } = actions;
    const create = useCreateComment(programId, target);
    const update = useUpdateComment(programId, target);
    const remove = useDeleteComment(programId, target);
    const upvote = useSetCommentUpvote(programId, target);
    const [mode, setMode] = useState('');
    const mine = !c.imported && !c.deleted && !!me && c.authorUserId === me;
    const isTop = c.parentId === '';
    const name = authorOf(c);
    return /*#__PURE__*/ _jsxs("li", {
        className: "flex min-w-0 items-start gap-[var(--space-2-5)]",
        "data-testid": Testids.cmComments.item,
        "data-row-id": c.id,
        "data-imported": c.imported ? 'true' : undefined,
        children: [
            /*#__PURE__*/ _jsx(SpecraAvatar, {
                name: name,
                size: "sm",
                className: "mt-[var(--space-0-5)]"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "min-w-0 flex-1",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-center gap-x-[var(--space-2)] gap-y-[var(--space-0-5)]",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                size: "caption",
                                className: "font-medium",
                                "data-testid": Testids.cmComments.author,
                                children: name
                            }),
                            c.imported ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "neutral",
                                "data-testid": Testids.cmComments.imported,
                                children: "From the old notes field"
                            }) : null,
                            c.viaAssist ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: "neutral",
                                "data-testid": Testids.cmComments.viaAssist,
                                children: "via Assist"
                            }) : null,
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                size: "micro",
                                tone: "muted",
                                title: new Date(c.createdAt).toLocaleString(),
                                children: [
                                    when(c.createdAt),
                                    c.editedAt ? ' · edited' : ''
                                ]
                            })
                        ]
                    }),
                    mode === 'edit' ? /*#__PURE__*/ _jsx("div", {
                        className: "mt-[var(--space-1)]",
                        children: /*#__PURE__*/ _jsx(Composer, {
                            placeholder: "Edit your comment",
                            initial: c.body,
                            submitLabel: "Save",
                            busy: update.isPending,
                            autoFocus: true,
                            onSubmit: (body)=>update.mutateAsync({
                                    commentId: c.id,
                                    body
                                }).then(()=>setMode('')),
                            onCancel: ()=>setMode(''),
                            textareaTestId: Testids.cmComments.editComposer,
                            submitTestId: Testids.cmComments.editSave
                        })
                    }) : /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "body-ui",
                        tone: c.deleted ? 'muted' : 'primary',
                        className: "mt-[var(--space-0-5)] whitespace-pre-wrap break-words",
                        "data-testid": Testids.cmComments.body,
                        children: c.deleted ? 'This comment was deleted.' : c.body
                    }),
                    !c.deleted && !c.imported && mode !== 'edit' ? /*#__PURE__*/ _jsxs("div", {
                        className: "mt-[var(--space-1)] flex flex-wrap items-center gap-[var(--space-1)]",
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraButton, {
                                size: "xs",
                                variant: c.upvotedByMe ? 'secondary' : 'ghost',
                                "aria-pressed": c.upvotedByMe,
                                "aria-label": `${c.upvotedByMe ? 'Remove your upvote' : 'Upvote'} (${c.upvoteCount})`,
                                disabled: upvote.isPending,
                                onClick: ()=>upvote.mutate({
                                        commentId: c.id,
                                        upvoted: !c.upvotedByMe
                                    }),
                                "data-testid": Testids.cmComments.upvote,
                                children: [
                                    /*#__PURE__*/ _jsx(ThumbsUp, {
                                        "aria-hidden": "true",
                                        className: c.upvotedByMe ? 'fill-current' : undefined
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "tabular-nums",
                                        children: c.upvoteCount
                                    })
                                ]
                            }),
                            isTop && actions.canComment ? /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "xs",
                                variant: "ghost",
                                onClick: ()=>setMode(mode === 'reply' ? '' : 'reply'),
                                "data-testid": Testids.cmComments.reply,
                                children: "Reply"
                            }) : null,
                            mine ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "xs",
                                        variant: "ghost",
                                        onClick: ()=>setMode('edit'),
                                        "data-testid": Testids.cmComments.edit,
                                        children: "Edit"
                                    }),
                                    mode === 'delete' ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "xs",
                                                variant: "destructive",
                                                disabled: remove.isPending,
                                                onClick: ()=>remove.mutate(c.id, {
                                                        onSuccess: ()=>setMode('')
                                                    }),
                                                "data-testid": Testids.cmComments.deleteConfirm,
                                                children: "Delete this comment"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "xs",
                                                variant: "ghost",
                                                onClick: ()=>setMode(''),
                                                children: "Keep it"
                                            })
                                        ]
                                    }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "xs",
                                        variant: "ghost",
                                        onClick: ()=>setMode('delete'),
                                        "data-testid": Testids.cmComments.delete,
                                        children: "Delete"
                                    })
                                ]
                            }) : null
                        ]
                    }) : null,
                    replies.length > 0 || mode === 'reply' ? /*#__PURE__*/ _jsxs("ul", {
                        className: "mt-[var(--space-2)] space-y-[var(--space-3)] border-l border-[var(--border-card)] pl-[var(--space-3)]",
                        children: [
                            replies.map((r)=>/*#__PURE__*/ _jsx(CommentItem, {
                                    c: r,
                                    replies: [],
                                    actions: actions
                                }, r.id)),
                            mode === 'reply' ? /*#__PURE__*/ _jsx("li", {
                                children: /*#__PURE__*/ _jsx(Composer, {
                                    placeholder: `Reply to ${name}`,
                                    submitLabel: "Reply",
                                    busy: create.isPending,
                                    autoFocus: true,
                                    onSubmit: (body)=>create.mutateAsync({
                                            body,
                                            parentId: c.id
                                        }).then(()=>setMode('')),
                                    onCancel: ()=>setMode(''),
                                    textareaTestId: Testids.cmComments.replyComposer,
                                    submitTestId: Testids.cmComments.replyPost
                                })
                            }) : null
                        ]
                    }) : null
                ]
            })
        ]
    });
}
export function CommentThread({ programId, target, className }) {
    const { user } = useAuth();
    const canComment = useProgramWriteAccess(programId).comment;
    const q = useComments(programId, target);
    const create = useCreateComment(programId, target);
    const all = q.data ?? [];
    const top = all.filter((c)=>c.parentId === '');
    const repliesOf = (id)=>all.filter((c)=>c.parentId === id);
    const live = all.filter((c)=>!c.deleted).length;
    const actions = {
        programId,
        target,
        me: user?.id ?? '',
        canComment
    };
    return /*#__PURE__*/ _jsxs("section", {
        className: className ?? 'space-y-[var(--space-3)]',
        "data-testid": Testids.cmComments.thread,
        "data-target-type": target.type,
        "data-target-id": target.id,
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: `Comments · ${live}`
            }),
            q.isLoading ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "caption",
                tone: "muted",
                children: "Loading comments…"
            }) : q.error ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "caption",
                tone: "danger",
                role: "alert",
                children: "Could not load the comments."
            }) : top.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "caption",
                tone: "muted",
                children: canComment ? 'No comments yet. Start the conversation below.' : 'No comments yet.'
            }) : /*#__PURE__*/ _jsx("ul", {
                className: "space-y-[var(--space-4)]",
                children: top.map((c)=>/*#__PURE__*/ _jsx(CommentItem, {
                        c: c,
                        replies: repliesOf(c.id),
                        actions: actions
                    }, c.id))
            }),
            canComment ? /*#__PURE__*/ _jsx(Composer, {
                placeholder: "Add a comment",
                submitLabel: "Comment",
                busy: create.isPending,
                onSubmit: (body)=>create.mutateAsync({
                        body
                    }),
                textareaTestId: Testids.cmComments.composer,
                submitTestId: Testids.cmComments.post
            }) : null
        ]
    });
}
